import React, { useState, useEffect, useCallback, useRef } from 'react';
import { auth } from '../firebase';
import { t } from '@lingui/core/macro';
import { CircularProgress } from '@mui/material';
import { getApiUrl } from '../utils/api';
import { DEFAULT_COLUMNS, LoaderOnFileUpload, extractDataFromRows } from './shared';
import { useProject } from '../contexts/ProjectContext';

interface GoogleSheetsLoaderProps {
  onFileUpload: LoaderOnFileUpload;
  hasExistingData: boolean;
  onSheetsUrlUpdate?: (url: string | null) => void;
  initialTemplateSpreadsheetId?: string;
  onInitialTemplateConsumed?: () => void;
  onAutoLoadStateChange?: (state: 'loading' | 'ready') => void;
  loadButtonRef?: React.RefObject<HTMLButtonElement | null>;
}

const GoogleSheetsLoader: React.FC<GoogleSheetsLoaderProps> = ({
  onFileUpload,
  hasExistingData,
  onSheetsUrlUpdate,
  initialTemplateSpreadsheetId,
  onInitialTemplateConsumed,
  onAutoLoadStateChange,
  loadButtonRef,
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [hasGoogleSheetsData, setHasGoogleSheetsData] = useState<boolean>(false);
  const hasAutoLoadedRef = useRef<boolean>(false);
  const { selectedProject } = useProject();

  const processResult = useCallback((result: unknown): void => {
    const r = result as { success?: boolean; data?: unknown[]; sheetName?: string; spreadsheetUrl?: string } | null;
    if (!r?.success || !r.data) throw new Error('Invalid response from server');

    const jsonData: unknown[] = r.data;
    if (!Array.isArray(jsonData) || jsonData.length === 0) throw new Error('No data found in Google Sheets');

    const { allSheetDataRows, blocks, videoSettings, columns, cardGroups } = extractDataFromRows(jsonData);

    // Update the spreadsheet URL if available
    if (r.spreadsheetUrl && onSheetsUrlUpdate) {
      onSheetsUrlUpdate(r.spreadsheetUrl);
    }

    onFileUpload(
      allSheetDataRows,
      columns.length ? columns : DEFAULT_COLUMNS,
      r.sheetName || 'Sheet1',
      blocks.length > 0 ? blocks : undefined,
      videoSettings,
      cardGroups
    );
  }, [onFileUpload, onSheetsUrlUpdate]);

  const fetchSheets = useCallback(async (userEmail: string, isManual: boolean): Promise<void> => {
    if (!isManual && (hasExistingData || hasAutoLoadedRef.current)) {
      setHasGoogleSheetsData(true);
      onAutoLoadStateChange?.('ready');
      return;
    }

    if (!isManual) {
      onAutoLoadStateChange?.('loading');
    }

    setIsLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        userEmail,
        projectId: selectedProject!.id,
        ...(initialTemplateSpreadsheetId ? { templateSpreadsheetId: initialTemplateSpreadsheetId } : {}),
      });
      const response = await fetch(`${getApiUrl()}/load-google-sheets?${params}`);

      if (response.status === 404) {
        setHasGoogleSheetsData(false);
        if (!isManual) {
          onAutoLoadStateChange?.('ready');
        }
        return;
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to load Google Sheets data');
      }

      const result = await response.json();
      onInitialTemplateConsumed?.();
      setHasGoogleSheetsData(true);
      processResult(result);
      if (!isManual) {
        hasAutoLoadedRef.current = true;
        onAutoLoadStateChange?.('ready');
      }

    } catch (err) {
      setHasGoogleSheetsData(false);
      setError(err instanceof Error ? err.message : 'Failed to load Google Sheets data');
      console.error('Error loading from Google Sheets:', err);
      if (!isManual) {
        onAutoLoadStateChange?.('ready');
      }
    } finally {
      setIsLoading(false);
    }
  }, [processResult, hasExistingData, selectedProject, initialTemplateSpreadsheetId, onInitialTemplateConsumed, onAutoLoadStateChange]);

  useEffect(() => {
    hasAutoLoadedRef.current = false;
  }, [selectedProject?.id]);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user?.email) {
        fetchSheets(user.email, false);
      } else {
        setHasGoogleSheetsData(false);
        onAutoLoadStateChange?.('ready');
      }
    });
    return () => unsubscribe();
  }, [fetchSheets, onAutoLoadStateChange]);

  const handleClick = (): void => {
    const email = auth.currentUser?.email;
    if (!email) { setError('No user email available'); return; }
    fetchSheets(email, true);
  };

  return (
    <div className="google-sheets-loader">
      <button ref={loadButtonRef} className="load-button" onClick={handleClick} disabled={isLoading}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', width: '100%' }}>
          {isLoading ? (
            <>
              <CircularProgress size={16} style={{ marginRight: '8px' }} />
              {t`Loading from Google Sheets...`}
            </>
          ) : (
            <>
              <span style={{ marginRight: '8px' }}>🔄</span>
              {hasGoogleSheetsData ? t`Load from Google Sheets` : t`No Google Sheets data available`}
            </>
          )}
        </div>
      </button>
      {error && <div style={{ color: 'red', marginTop: '8px', fontSize: '14px' }}>{error}</div>}
    </div>
  );
};

export default GoogleSheetsLoader;
